import styled, { keyframes, css } from 'styled-components'

const spin = keyframes`
  to { transform: rotate(360deg); }
`

/* Supabase-inspired: phosphor green CTAs, flat bordered surfaces, no elevation */
const variants = {
  primary: css`
    background: ${({ theme }) => theme.colors.action.primary.bg};
    color: ${({ theme }) => theme.colors.action.primary.text};
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    box-shadow: 0 0 0 0 transparent;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.primary.bgHover};
      border-color: ${({ theme }) => theme.colors.action.primary.bgActive};
      box-shadow: 0 0 0 3px rgba(62, 207, 142, 0.22);
      filter: brightness(1.04);
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.primary.bgActive};
      border-color: ${({ theme }) => theme.colors.action.primary.borderHover};
      box-shadow: 0 0 0 2px rgba(62, 207, 142, 0.16);
      filter: brightness(0.96);
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
      outline-offset: 2px;
    }
  `,

  danger: css`
    background: ${({ theme }) => theme.colors.action.danger.bg};
    color: ${({ theme }) => theme.colors.action.danger.text};
    border-color: ${({ theme }) => theme.colors.action.danger.bg};
    box-shadow: 0 0 0 0 transparent;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.danger.bgHover};
      border-color: ${({ theme }) => theme.colors.action.danger.bgActive};
      box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.action.danger.bg}2e`};
      filter: brightness(1.04);
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.danger.bgActive};
      border-color: ${({ theme }) => theme.colors.action.danger.bgActive};
      box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.action.danger.bg}24`};
      filter: brightness(0.96);
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.action.danger.bg};
      outline-offset: 2px;
    }
  `,

  secondary: css`
    background: ${({ theme }) => theme.colors.action.neutral.bg};
    color: ${({ theme }) => theme.colors.action.neutral.text};
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: 0 0 0 0 transparent;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.neutral.bgHover};
      border-color: ${({ theme }) => theme.colors.borderStrong};
      box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.borderStrong}40`};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.action.neutral.bgActive};
      border-color: ${({ theme }) => theme.colors.borderStrong};
      box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.borderStrong}33`};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.borderStrong};
      outline-offset: 2px;
    }
  `,
}

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  min-width: 5.5rem;
  min-height: 1.875rem;
  padding: 0.3rem 0.75rem;

  border: 1px solid transparent;
  border-radius: 9999px;

  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.35;
  white-space: nowrap;

  cursor: pointer;
  user-select: none;
  box-shadow: none;
  transition:
    background 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    filter 0.18s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ variant = 'primary' }) => variants[variant]}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    filter: none;
  }

  ${({ prefix }) =>
    prefix &&
    css`
      &::before {
        content: '${prefix}';
        margin-right: 0.15rem;
        opacity: 0.9;
      }
    `}
`

export const Spinner = styled.div`
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
  flex-shrink: 0;
  opacity: 0.7;
`
